/**
 * kumo.sanctions — プレイヤー本人が自分への制裁（BAN・一時停止など）を見て、異議を申し立てる（2026-09-27 appeal-tools・Quality SEC-HIDE-03）。
 * サーバーは apps/server/src/modules/integrity/routes.ts（GET /v1/players/me/sanctions・POST …/:sanctionId/appeal・POST /v1/auth/appeal）。
 *
 *   const { sanctions } = await kumo.sanctions.mine();                 // BAN の直後（手元のセッションが生きている間）に読める
 *   const ban = sanctions.find((s) => s.kind === 'ban' && s.active);
 *   if (ban && !ban.appeal) await kumo.sanctions.appeal(ban.id, 'It was my little brother');
 *   // id を読めなかった（BAN から 15 分を過ぎた）ときは null で: await kumo.sanctions.appeal(null, 'It was my little brother');
 *   // 回答はクリエイターがダッシュボード / MCP で出す。結果は mine() の appeal.status（accepted = BAN が解けた）と appeal.response
 *
 * WHY moderation（通報・制裁を掛ける）から分けて出すか（founder 2026-09-27・MASTER-PLAN §1c「プレイヤーの BAN は残す」）:
 *   最初の公開では通報と制裁を掛ける機能を見せない（launchHidden.ts の 'moderation' = kumo.reports）。一方で BAN は残すので、
 *   誤った BAN を本人が取り消してもらう道が要る。サーバーはこの 3 本を機能の切り替えの外（featureGate.ts の ALWAYS_ON）に置いている
 *   = SDK も切り替えの外に置く（social と同じ扱い）。以前は social.ts の中にあり、moderation と一緒に隠れていた（SEC-HIDE-03）。
 * ここには「読む」と「申し立てる」だけを置く。制裁を掛ける・取り消す・通報はプレイヤーの SDK に置かない（運営の道具 = ダッシュボード・MCP）。
 * 小さい（呼び出し 2 本）ので lazy にしない。
 */

import type { KumoCore } from './core.js';
import { KumoError } from './core.js';

/** 本人に見える制裁（運営の内部メモ reason は含まれない。見せる文は publicNote） */
export interface Sanction {
  id: string;
  kind: 'warning' | 'suspension' | 'ban' | 'leaderboard_exclusion' | 'chat_mute';
  publicNote: string | null;
  createdAt: string;
  expiresAt: string | null;
  active: boolean;
  revoked: boolean;
  appeal: { status: 'open' | 'accepted' | 'rejected'; response: string | null } | null;
}

export interface AppealResult {
  appealId: string;
  status: 'open';
}

export function createSanctions(core: KumoCore) {
  return {
    /**
     * 自分への制裁の一覧。BAN の 403 には制裁の ID が無いので、申し立てに要る id はここで知る。
     * BAN された後は、手元のセッション（アクセストークン・最長 15 分）が切れると読めなくなる = BAN に気づいたらすぐ読んで id を覚えておく
     */
    mine: () => core.request<{ sanctions: Sanction[] }>('GET', '/v1/players/me/sanctions'),
    /**
     * このブラウザから、ログインできない人の口（POST /v1/auth/appeal）で異議を出せる**可能性がある**か（2026-09-28・Core と合意）。
     * = BAN で失効したリフレッシュトークン（core.ts の appealToken）を手元に持っているか。true なら `appeal(null, message)` が使える。
     * WHY: BAN から 15 分を過ぎた起動では SDK が新しいゲストで始め、mine() も読めない = ページは「異議の画面を出すべきか」を知る手段が無かった
     *   （skills/user-login を作ったときに見つかった）。
     * 約束（Core の条件）: ① boolean だけを返す（トークン・制裁の中身・期限はページのスクリプトに渡さない）
     *   ② true でも「BAN されている」とは言い切らない（サーバーの受付の期間外・既に解けている、がある）。文言は「異議を出せる可能性がある」にし、
     *      結果は appeal() の応答で決める ③ appeal が成功・既に申し立て済み（409）・期限切れや対象外（401）で終わったら、手元のトークンを消して false に戻る
     */
    hasPendingAppeal: (): boolean => core.appealToken() !== null,
    /**
     * 制裁 1 件に 1 回だけ異議を申し立てる（2 回目は 409 already_appealed）。message は 2000 文字まで。
     * 口は 2 つあり、SDK が選ぶ:
     *   1. BAN で失効したリフレッシュトークンが手元に残っていれば（BAN の後の起動 = core.ts の appealToken）、それを本人の証明にして
     *      POST /v1/auth/appeal（ログインできない人の口）。WHY 先に試すか: BAN の後の起動では SDK が既定で新しいゲストを作るので、
     *      今のセッションは BAN された本人ではない（2 の口では他人の制裁として 404 になる）
     *   2. それが無い・使えない（401）なら、今のセッションで POST /v1/players/me/sanctions/:id/appeal（BAN の直後でセッションが生きている間）
     *
     * sanctionId は分からなければ null でよい（2026-09-28・Core 840c5b7 で /v1/auth/appeal の sanctionId が省略可に）。
     * WHY: BAN から 15 分を過ぎるとアクセストークンが切れて mine() が読めず、本人は id を知る手段が無い。省くとサーバーが
     *   「そのリフレッシュトークンを失効させた時刻の前後の、最新の有効な BAN・停止」を対象にする。1 の口でだけ使える
     *   （2 の口は URL に id が要る）→ id が無く 1 の口も使えないときは 0 sanction_unknown（先に mine() で id を知ってもらう）
     */
    async appeal(sanctionId: string | null | undefined, message: string): Promise<AppealResult> {
      const saved = core.appealToken();
      if (saved) {
        try {
          const r = await core.request<AppealResult>('POST', '/v1/auth/appeal', sanctionId ? { refreshToken: saved, sanctionId, message } : { refreshToken: saved, message }, { auth: false });
          core.clearAppealToken();
          return r;
        } catch (e) {
          // 401 = このトークンは BAN の失効ではない（期限切れ・別の制裁）→ 捨てて今のセッションの口へ。
          // 409 already_appealed = このトークンで出せる異議はもう無い → 捨てる（hasPendingAppeal を false に戻す・Core の条件 ③）。それ以外はそのまま返す
          if (e instanceof KumoError && e.status === 409) core.clearAppealToken();
          if (!(e instanceof KumoError) || e.status !== 401) throw e;
          core.clearAppealToken();
        }
      }
      if (!sanctionId) {
        throw new KumoError(0, 'sanction_unknown', 'Which sanction to appeal is unknown. Read kumo.sanctions.mine() while the session is still valid and pass its id.');
      }
      return core.request<AppealResult>('POST', `/v1/players/me/sanctions/${encodeURIComponent(sanctionId)}/appeal`, { message });
    }
  };
}

export type KumoSanctions = ReturnType<typeof createSanctions>;
